import React, { useState, useEffect, useRef, useCallback } from "react";
import { Spinner, Alert, Button, ButtonGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as pdfjsLib from "pdfjs-dist";
import { getJwt } from "src/auth";

// Set up pdf.js worker (matches PDFDocumentViewer)
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface HighlightBbox {
  page: number; // 1-indexed
  x0: number; // [0,1] ratio of page width (left)
  y0: number; // [0,1] ratio of page height, top-down
  x1: number;
  y1: number;
}

interface DocumentViewerProps {
  documentId: string;
  apiBaseUrl?: string;
  authToken?: string;
  expiresIn?: number;
  width?: string;
  height?: string;
  showHeader?: boolean;
  useSandbox?: boolean;
  pageNumber?: number; // Optional page number for PDFs (1-indexed)
  highlightBbox?: HighlightBbox | null;
}

interface PresignedUrlResponse {
  url: string;
  expiresIn: number;
  document: {
    id: string;
    originalname: string;
    mimetype: string;
  };
}

const isImageMimetype = (mimetype: string): boolean => {
  const imageMimetypes = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/tiff",
    "image/bmp",
    "image/svg+xml",
  ];
  return imageMimetypes.includes(mimetype?.toLowerCase());
};

const isPdfMimetype = (mimetype: string | undefined): boolean => {
  if (!mimetype) return false;
  return mimetype.toLowerCase().includes("pdf");
};

export const PresignedDocumentViewer: React.FC<DocumentViewerProps> = ({
  documentId,
  apiBaseUrl = "/api/v1/protected",
  expiresIn = 3600,
  width = "100%",
  height = "100vh",
  showHeader = false,
  useSandbox = false,
  pageNumber,
  highlightBbox = null,
}) => {
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentInfo, setDocumentInfo] = useState<any>(null);
  const [imageLoadError, setImageLoadError] = useState(false);

  // PDF (pdfjs) state
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pdfRenderError, setPdfRenderError] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(pageNumber || 1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.2);
  const [renderedViewport, setRenderedViewport] =
    useState<pdfjsLib.PageViewport | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

  const authToken = getJwt();

  // Fetch presigned URL
  useEffect(() => {
    const fetchPresignedUrl = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        const response = await fetch(
          `${apiBaseUrl}/document/${documentId}/downloads3`,
          {
            method: "GET",
            headers,
            credentials: "include",
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch presigned URL: ${response.statusText}`
          );
        }

        const data: PresignedUrlResponse = await response.json();
        setPresignedUrl(data.url);
        setDocumentInfo(data.document);
        setImageLoadError(false);
        setPdfRenderError(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        console.error("Error fetching presigned URL:", err);
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      fetchPresignedUrl();
    }
  }, [documentId, apiBaseUrl, authToken, expiresIn]);

  // Load PDF via pdfjs once we have a URL + it's a PDF
  useEffect(() => {
    if (!presignedUrl || !documentInfo) return;
    if (!isPdfMimetype(documentInfo.mimetype)) return;

    let cancelled = false;
    let loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;

    const loadPdf = async () => {
      try {
        const resp = await fetch(presignedUrl);
        if (!resp.ok) throw new Error("Failed to fetch PDF bytes");
        const ab = await resp.arrayBuffer();
        if (cancelled) return;

        loadingTask = pdfjsLib.getDocument({
          data: ab,
          disableWorker: true,
        } as any);
        const pdf = await loadingTask.promise;
        if (cancelled) return;
        setPdfDoc(pdf);
        setTotalPages(pdf.numPages);
        // Initialize page from prop if provided, clamped
        const initialPage = Math.min(
          Math.max(1, pageNumber || 1),
          pdf.numPages
        );
        setCurrentPage(initialPage);
      } catch (e) {
        console.error("[PresignedDocumentViewer] pdfjs load failed:", e);
        if (!cancelled) setPdfRenderError(true);
      }
    };

    loadPdf();
    return () => {
      cancelled = true;
      if (loadingTask) {
        try {
          loadingTask.destroy();
        } catch (_) {
          /* noop */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presignedUrl, documentInfo]);

  // When `pageNumber` prop changes externally, follow it.
  useEffect(() => {
    if (!pageNumber) return;
    if (totalPages > 0) {
      setCurrentPage(Math.min(Math.max(1, pageNumber), totalPages));
    } else {
      setCurrentPage(Math.max(1, pageNumber));
    }
  }, [pageNumber, totalPages]);

  // When highlightBbox changes, jump to its page.
  useEffect(() => {
    if (!highlightBbox) return;
    if (totalPages > 0) {
      setCurrentPage(
        Math.min(Math.max(1, highlightBbox.page), totalPages)
      );
    }
  }, [highlightBbox, totalPages]);

  // Render the current page on canvas
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;

    const renderPage = async () => {
      try {
        if (renderTaskRef.current) {
          try {
            renderTaskRef.current.cancel();
          } catch (_) {
            /* noop */
          }
        }
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;
        const naturalViewport = page.getViewport({ scale: 1 });
        const naturalRotation = naturalViewport.rotation || 0;
        const viewport = page.getViewport({
          scale,
          rotation: naturalRotation,
        });
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext("2d")!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        // CSS sizing is driven by the wrapper (width:100%, height:auto) so
        // elongated pages keep aspect ratio and overflow vertically with scroll.
        canvas.style.width = "100%";
        canvas.style.height = "auto";
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        if (cancelled) return;
        setRenderedViewport(viewport);
      } catch (err: any) {
        if (
          err &&
          err.message &&
          !String(err.message).toLowerCase().includes("cancel")
        ) {
          console.error("[PresignedDocumentViewer] render error:", err);
        }
      }
    };

    renderPage();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage, scale]);

  // Keyboard zoom shortcuts (defensive: only when pdfDoc visible)
  const handleZoomIn = useCallback(
    () => setScale((s) => Math.min(3, +(s + 0.2).toFixed(2))),
    []
  );
  const handleZoomOut = useCallback(
    () => setScale((s) => Math.max(0.4, +(s - 0.2).toFixed(2))),
    []
  );

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center bg-light"
        style={{ width, height }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ width, height }}
      >
        <div className="text-center p-4" style={{ maxWidth: "500px" }}>
          <Alert variant="danger">
            <Alert.Heading>
              <i className="fa fa-exclamation-triangle mr-2"></i>
              Failed to load document
            </Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        </div>
      </div>
    );
  }

  if (!presignedUrl) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center bg-light text-center px-4"
        style={{ width, height }}
      >
        <FontAwesomeIcon
          icon="file-pdf"
          className="text-muted mb-3"
          style={{ fontSize: "2.5rem" }}
        />
        <p className="text-muted mb-1" style={{ fontWeight: 600 }}>
          Source PDF unavailable
        </p>
        <p className="text-muted small mb-0" style={{ maxWidth: 360 }}>
          The original document file couldn't be loaded — the data on the
          right was extracted from a previous upload. Re-upload the file to
          re-link it, or contact support if the document was deleted in
          error.
        </p>
      </div>
    );
  }

  const headerHeight = showHeader && documentInfo ? 40 : 0;
  const contentHeight =
    height === "100vh" ? `calc(${height} - ${headerHeight}px)` : height;

  const isImage = documentInfo && isImageMimetype(documentInfo.mimetype);
  const isPdf = documentInfo && isPdfMimetype(documentInfo.mimetype);

  // Decide which renderer to use for PDFs:
  // - Try pdfjs canvas (supports bbox overlay).
  // - Fall back to iframe if pdfjs fails to load the doc.
  const usePdfJs = isPdf && !pdfRenderError;

  // Compute bbox overlay rect using rendered viewport size.
  const overlayStyle: React.CSSProperties | null =
    highlightBbox &&
    renderedViewport &&
    highlightBbox.page === currentPage
      ? {
          position: "absolute",
          left: `${highlightBbox.x0 * 100}%`,
          top: `${highlightBbox.y0 * 100}%`,
          width: `${Math.max(0, highlightBbox.x1 - highlightBbox.x0) * 100}%`,
          height: `${Math.max(0, highlightBbox.y1 - highlightBbox.y0) * 100}%`,
          border: "2px solid #f59e0b",
          backgroundColor: "rgba(245, 158, 11, 0.18)",
          pointerEvents: "none",
          boxShadow: "0 0 0 1px rgba(0,0,0,0.06)",
          transition: "all 120ms ease-out",
          zIndex: 5,
        }
      : null;

  return (
    <div className="position-relative" style={{ width, height }}>
      {showHeader && documentInfo && (
        <div
          className="bg-dark text-white px-3 py-2 d-flex align-items-center"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: `${headerHeight}px`,
            zIndex: 10,
            fontSize: "14px",
          }}
        >
          <span className="font-weight-bold">{documentInfo.originalname}</span>
          <span className="ml-3 text-muted">({documentInfo.mimetype})</span>
        </div>
      )}

      {isImage && !imageLoadError ? (
        <div
          style={{
            width: "100%",
            height: contentHeight,
            marginTop: headerHeight ? `${headerHeight}px` : "0",
            overflow: "auto",
            backgroundColor: "#f5f5f5",
          }}
        >
          <img
            src={presignedUrl}
            alt={documentInfo?.originalname || "Document image"}
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "0 auto",
            }}
            onError={() => setImageLoadError(true)}
          />
        </div>
      ) : usePdfJs ? (
        <div
          style={{
            width: "100%",
            height: contentHeight,
            marginTop: headerHeight ? `${headerHeight}px` : "0",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#525659",
          }}
        >
          {/* Toolbar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "4px 8px",
              backgroundColor: "#323639",
              color: "#fff",
              fontSize: 12,
              gap: 8,
            }}
          >
            <ButtonGroup size="sm">
              <Button
                variant="outline-light"
                disabled={!pdfDoc || currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                title="Previous page"
              >
                <FontAwesomeIcon icon="chevron-left" />
              </Button>
              <Button
                variant="outline-light"
                disabled={!pdfDoc || currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                title="Next page"
              >
                <FontAwesomeIcon icon="chevron-right" />
              </Button>
            </ButtonGroup>
            <span>
              Page {currentPage}
              {totalPages ? ` / ${totalPages}` : ""}
            </span>
            <ButtonGroup size="sm">
              <Button
                variant="outline-light"
                onClick={handleZoomOut}
                title="Zoom out"
              >
                <FontAwesomeIcon icon="search-minus" />
              </Button>
              <Button
                variant="outline-light"
                onClick={handleZoomIn}
                title="Zoom in"
              >
                <FontAwesomeIcon icon="search-plus" />
              </Button>
            </ButtonGroup>
          </div>

          {/* Canvas + overlay container */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              padding: 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            {!pdfDoc ? (
              <div className="text-center text-light p-4">
                <Spinner animation="border" variant="light" />
                <div className="mt-2">Rendering PDF…</div>
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  display: "block",
                  width: "100%",
                  maxWidth: renderedViewport
                    ? `${renderedViewport.width}px`
                    : undefined,
                  margin: "0 auto",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.4)",
                }}
              >
                <canvas
                  ref={canvasRef}
                  style={{
                    display: "block",
                    width: "100%",
                    height: "auto",
                  }}
                />
                {overlayStyle && <div style={overlayStyle} />}
              </div>
            )}
          </div>
        </div>
      ) : (
        // Iframe fallback (non-PDF non-image, or pdfjs failed)
        <iframe
          src={
            pageNumber && !isImage
              ? `${presignedUrl}#page=${pageNumber}&toolbar=0`
              : presignedUrl
          }
          style={{
            width: "100%",
            height: contentHeight,
            marginTop: headerHeight ? `${headerHeight}px` : "0",
            border: "none",
          }}
          title={documentInfo?.originalname || "Document viewer"}
          {...(useSandbox && {
            sandbox: "allow-same-origin allow-scripts allow-popups allow-forms",
          })}
          allowFullScreen
        >
          Your browser does not support iframes. Please download the document to
          view it: <a href={presignedUrl}>Download Document</a>
        </iframe>
      )}
    </div>
  );
};

export default PresignedDocumentViewer;
